// Ported from eb-ui `src/legacy/components/CollectionHero/CollectionHero.tsx`.
// The legacy `import '../../styles/main.scss'` is REMOVED — the app's
// `src/styles/cc.scss` (loaded once in `_app`) already compiles the `cc-hero*`
// rules, so re-importing would double-load the styles.
import React, { ReactNode, useContext } from 'react';

import { AUTOCREATED_COLLECTION_DEFAULT_IMAGE_URL } from '../../_shared/constants';
import {
    isAutocreatedCollectionPreviewWithTips,
    isAutocreatedCollectionUnedited,
} from '../../_shared/utils';
import { ApplicationContext } from '../../context';
import type { CollectionServerData } from '@/types';

export const CollectionHero: React.FC<{ children?: ReactNode }> = ({
    children,
}) => {
    const context = useContext(ApplicationContext) as
        | CollectionServerData
        | null;
    const isPlaceholderHero =
        isAutocreatedCollectionUnedited(context?.collection) &&
        isAutocreatedCollectionPreviewWithTips(context);
    const heroImage = isPlaceholderHero
        ? AUTOCREATED_COLLECTION_DEFAULT_IMAGE_URL
        : context?.collection?.image?.url;
    // The API's default `url` is signed at w=512; rendering it across a
    // full-width 3:1 band upscales ~3x and looks blurry. The original
    // (full-res, its own valid signature) is the sharp large candidate —
    // the CSS `object-fit: cover` crops it to the band exactly like the
    // legacy w=1024 crop did. BUGBOT (Low): never pair the large ORIGINAL
    // with the placeholder swap — the srcSet would render the placeholder
    // at 512w and the real image at 1272w (wrong hero on wide viewports).
    const heroImageLarge = isPlaceholderHero
        ? undefined
        : context?.collection?.image?.originalUrl;

    return (
        <div role="banner" className="cc-hero">
            {heroImage ? (
                <div aria-label="Hero Image" className="cc-hero__image-wrapper">
                    <picture data-testid="collection-hero-image">
                        <source
                            srcSet={
                                heroImageLarge
                                    ? `${heroImage} 512w, ${heroImageLarge} 1272w`
                                    : heroImage
                            }
                            sizes="100vw"
                        />
                        <img alt={''} />
                    </picture>
                </div>
            ) : null}

            <div
                className="cc-hero__button-share"
                aria-label="Share collection"
            >
                {children}
            </div>
        </div>
    );
};
